import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { services } from "@/data/services";
import { useLang } from "@/context/LanguageContext";
import { Film, Camera, Clapperboard, Boxes, Sparkles, ArrowUpRight, CheckCircle2, Star, Aperture, Mic, Plane, Megaphone } from "lucide-react";

const iconMap = { Film, Camera, Clapperboard, Boxes, Sparkles, Star, Aperture, Mic, Plane, Megaphone };

const Services = () => {
  const { t, lang } = useLang();
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [hash]);

  return (
    <div data-testid="services-page">
      <section className="pt-20 pb-2 md:pt-24 md:pb-4">
        <div className="container-x max-w-5xl rtl:-translate-y-2 md:rtl:-translate-y-4">
          <div className="overline mb-6">{t("services.eyebrow")}</div>
          <h1 className="display-h1">{t("services.title")}</h1>
          <p className="mt-8 text-white/70 text-xl leading-relaxed max-w-3xl">{t("services.lead")}</p>
        </div>
      </section>

      {services.map((s, i) => {
        const Icon = iconMap[s.icon] || Sparkles;
        const flipped = i % 2 === 1;
        return (
          <section
            key={s.id}
            id={s.id}
            data-testid={`service-detail-${s.id}`}
            className={`${i === 0 ? "pt-4 pb-20 md:pt-6 md:pb-28" : "py-20 md:py-28"} ${i % 2 === 0 ? "" : "bg-[#0d0d10] border-y border-white/5"}`}
          >
            <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center rtl:items-start">
              <div className={`lg:col-span-6 ${flipped ? "lg:order-2" : "lg:order-1"}`}>
                <div className="aspect-[4/5] rounded-xl overflow-hidden relative border border-white/5">
                  <img src={s.image} alt={s.title[lang]} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C]/80 via-transparent to-transparent" />
                  <div className="absolute top-6 start-6 w-14 h-14 rounded-xl bg-[#E6A95B] text-[#0A0A0C] grid place-items-center">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="absolute bottom-6 start-6 text-[10px] uppercase tracking-[0.24em] text-white/80">0{i + 1} · {s.title[lang]}</div>
                </div>
              </div>

              <div className={`lg:col-span-6 ${flipped ? "lg:order-1" : "lg:order-2"}`}>
                <div className="overline mb-4">0{i + 1} — {t("services.itemsIntro")}</div>
                <h2 className="display-h2">{s.title[lang]}</h2>
                <p className="mt-3 font-display italic text-[#E6A95B] text-xl">{s.tagline[lang]}</p>
                <p className="mt-6 text-white/70 text-lg leading-relaxed">{s.desc[lang]}</p>

                <div className="mt-10">
                  <div className="overline mb-5">{t("services.processTitle")}</div>
                  <ol className="space-y-3">
                    {s.process.map((p, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-white/80">
                        <span className="text-[#E6A95B] font-mono text-sm mt-1">0{idx + 1}</span>
                        <span className="text-base md:text-lg">{p[lang]}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="mt-8 flex items-start gap-3 text-white/60 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-[#E6A95B] mt-1 shrink-0" />
                  <span>{s.deliverables[lang]}</span>
                </div>

                <div className="mt-10">
                  <Link to="/contact" data-testid={`service-cta-${s.id}`} className="btn-primary">
                    {t("services.requestQuote")} <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default Services;
